import React from 'react';
import {
  Input,
  DatePicker,
  TimePicker,
  Switch,
  Select,
  Button,
  Popconfirm,
  Collapse,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { CruisePortStatus, Trip, TripCityVisitMeta } from '../../types/client';
import { tripEditEn } from '../../content/trips.en';
import { isAtSeaLocation } from '../../services/cruiseItinerariesApi';
import {
  renameCity,
  setEmbarkDebark,
  updateCityMeta,
  removeCityFromDraft,
} from '../../utils/tripEdit';
import { formatCardWeekdayDate } from '../../utils/tripDayCards';
import styles from './EditTripCityPanel.module.css';

interface EditTripCityPanelProps {
  trip: Trip;
  cityId: string;
  onChange: (next: Trip) => void;
}

function ymd(d: Dayjs | null): string | null {
  return d ? d.format('YYYY-MM-DD') : null;
}

function hm(d: Dayjs | null): string | null {
  return d ? d.format('HH:mm') : null;
}

function toDay(iso: string | null | undefined): Dayjs | null {
  if (!iso) return null;
  const d = dayjs(iso);
  return d.isValid() ? d : null;
}

function toTime(hmValue: string | null | undefined): Dayjs | null {
  if (!hmValue) return null;
  const d = dayjs(hmValue, 'HH:mm');
  return d.isValid() ? d : null;
}

const EditTripCityPanel: React.FC<EditTripCityPanelProps> = ({ trip, cityId, onChange }) => {
  const name = trip.cityNames?.[cityId] ?? '';
  const meta: TripCityVisitMeta = trip.cityVisitMeta?.[cityId] ?? {
    arrivalDate: null,
    departureDate: null,
    portArrivalHm: null,
    portDepartureHm: null,
    cruisePortStatus: null,
  };
  const isCruise = trip.tripType === 'cruise';
  const atSea = isAtSeaLocation(name);
  const showDock = Boolean(meta.isEmbarkation || meta.isDebarkation);

  const patchMeta = (patch: Partial<TripCityVisitMeta>): void => {
    onChange(updateCityMeta(trip, cityId, patch));
  };

  const summaryDate = formatCardWeekdayDate(meta.arrivalDate);

  return (
    <Collapse
      className={styles.cityCollapse}
      items={[
        {
          key: cityId,
          label: (
            <div className={styles.cityLabel}>
              <span className={styles.cityLabelName}>{name || tripEditEn.cityNamePlaceholder}</span>
              {summaryDate ? <span className={styles.cityLabelDate}>{summaryDate}</span> : null}
            </div>
          ),
          children: (
            <div className={styles.cityFields}>
              {!atSea ? (
                <Input
                  value={name}
                  onChange={(e) => onChange(renameCity(trip, cityId, e.target.value))}
                  placeholder={
                    isCruise ? tripEditEn.portNamePlaceholder : tripEditEn.cityNamePlaceholder
                  }
                />
              ) : (
                <div className={styles.seaDayName}>{name}</div>
              )}

              {atSea ? (
                <div className={styles.fieldRow}>
                  <label>{tripEditEn.seaDayDate}</label>
                  <DatePicker
                    value={toDay(meta.arrivalDate)}
                    onChange={(d) => {
                      const date = ymd(d);
                      patchMeta({ arrivalDate: date, departureDate: date });
                    }}
                    style={{ width: '100%' }}
                  />
                </div>
              ) : (
                <>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.arrivalDate}</label>
                    <DatePicker
                      value={toDay(meta.arrivalDate)}
                      onChange={(d) => patchMeta({ arrivalDate: ymd(d) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.arrivalTime}</label>
                    <TimePicker
                      value={toTime(meta.portArrivalHm)}
                      format="HH:mm"
                      onChange={(d) => patchMeta({ portArrivalHm: hm(d) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.departureDate}</label>
                    <DatePicker
                      value={toDay(meta.departureDate)}
                      onChange={(d) => patchMeta({ departureDate: ymd(d) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.departureTime}</label>
                    <TimePicker
                      value={toTime(meta.portDepartureHm)}
                      format="HH:mm"
                      onChange={(d) => patchMeta({ portDepartureHm: hm(d) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                </>
              )}

              {isCruise && !atSea ? (
                <>
                  <div className={styles.switchRow}>
                    <span>{tripEditEn.embarkation}</span>
                    <Switch
                      checked={Boolean(meta.isEmbarkation)}
                      onChange={(v) => onChange(setEmbarkDebark(trip, cityId, 'isEmbarkation', v))}
                    />
                  </div>
                  <div className={styles.switchRow}>
                    <span>{tripEditEn.debarkation}</span>
                    <Switch
                      checked={Boolean(meta.isDebarkation)}
                      onChange={(v) => onChange(setEmbarkDebark(trip, cityId, 'isDebarkation', v))}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.portStatus}</label>
                    <Select
                      value={meta.cruisePortStatus ?? undefined}
                      allowClear
                      placeholder={tripEditEn.portStatus}
                      onChange={(v) =>
                        patchMeta({ cruisePortStatus: (v as CruisePortStatus) ?? null })
                      }
                      options={Object.entries(tripEditEn.portStatusOptions).map(([value, label]) => ({
                        value,
                        label,
                      }))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  {showDock ? (
                    <div className={styles.fieldRow}>
                      <label>{tripEditEn.dockNumber}</label>
                      <Input
                        value={meta.dockNumber ?? ''}
                        placeholder={tripEditEn.dockNumberPlaceholder}
                        onChange={(e) => patchMeta({ dockNumber: e.target.value || null })}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}

              <Popconfirm
                title={tripEditEn.removeCityConfirm}
                onConfirm={() => onChange(removeCityFromDraft(trip, cityId))}
              >
                <Button danger type="link" className={styles.removeCity}>
                  {tripEditEn.removeCity}
                </Button>
              </Popconfirm>
            </div>
          ),
        },
      ]}
    />
  );
};

export default EditTripCityPanel;
